import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Orca repo build mode — how orca builds against a registered repository.
 * @since 1.0.0
 */
export const OrcaRepoMode = Schema.Literal("default", "direct", "mirror");
export type OrcaRepoMode = Schema.Schema.Type<typeof OrcaRepoMode>;

/**
 * Orca repo merge method — how orca merges its PRs on a registered repository.
 * @since 1.0.0
 */
export const OrcaRepoMergeMethod = Schema.Literal("squash", "merge", "rebase");
export type OrcaRepoMergeMethod = Schema.Schema.Type<typeof OrcaRepoMergeMethod>;

/**
 * Orca repo agent CLI — the default agent CLI for builds on a repo.
 * @since 1.0.0
 */
export const OrcaRepoAgentCli = Schema.Literal("claude", "codex");
export type OrcaRepoAgentCli = Schema.Schema.Type<typeof OrcaRepoAgentCli>;

/**
 * OrcaRepo model — one row per repository orca can build against, holding the
 * operator-tunable per-repo configuration (mode, merge policy, worker image
 * pins, secrets reference, contract override, team-key routing defaults).
 *
 * @since 1.0.0
 */
export class OrcaRepo extends Model.Class<OrcaRepo>("OrcaRepo")({
  id: Model.Generated(Schema.String),

  repo: Schema.String,
  mode: OrcaRepoMode,

  defaultBranch: Schema.NullOr(Schema.String),

  mergeMethod: OrcaRepoMergeMethod,
  deleteBranch: Schema.Boolean,

  contract: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.Unknown })),

  secretsRepo: Schema.NullOr(Schema.String),
  env: Schema.NullOr(Schema.Record({ key: Schema.String, value: Schema.String })),

  defaultModel: Schema.NullOr(Schema.String),
  defaultAgentCli: Schema.NullOr(OrcaRepoAgentCli),

  maxAttempts: Schema.NullOr(Schema.Number),
  maxParallel: Schema.NullOr(Schema.Number),

  workerImage: Schema.NullOr(Schema.String),
  workerTag: Schema.NullOr(Schema.String),
  workerMemoryLimit: Schema.NullOr(Schema.String),

  teamKeys: Schema.NullOr(Schema.Array(Schema.String)),

  // Mirror mode pairing (mode === "mirror"): the client repo mirrored, the
  // branch fetched from it, and the mirror branch fast-forwarded. NULL for
  // direct/default repos. scripts/mirror-sync.ts keeps reading the in-image
  // contract file as its fallback source.
  upstream: Schema.NullOr(Schema.String),
  upstreamBranch: Schema.NullOr(Schema.String),
  mirrorBranch: Schema.NullOr(Schema.String),

  notes: Schema.NullOr(Schema.String),

  /**
   * OL-60: the customer that owns this repo; `null` = unowned legacy row.
   * `orca_repos.repo` is unique, so a repo has exactly one owner — this field
   * is the enforcement point for "this customer may build / arm a trigger on
   * this repo" (see `OrcaReposAccessor.listForCustomer`).
   *
   * @since 1.0.0
   */
  customerId: Schema.NullOr(Schema.String),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}

/**
 * Seen-repos read model — the union of every registered repo and every repo
 * orca has actually built against (`orca_tasks.repo` / `orca_projects.repo`),
 * one row per distinct `repo` slug. This is NOT a table row: it is the shape
 * returned by {@link OrcaReposAccessor.listSeenRepos} so the Repositories list
 * page can surface "seen, unconfigured" repos alongside registered ones.
 *
 * - `registered`: the repo has an `orca_repos` config row.
 * - `taskCount` / `runningCount`: total tasks and non-terminal (in-flight)
 *   tasks orca has dispatched for this repo.
 * - `lastActivityAt`: newest `orca_tasks.updated_at` for the repo, or `null`
 *   when the repo has no tasks (registered-only / project-only).
 * - `lastIssueIdentifier`: the Linear identifier of the most recently updated
 *   task, or `null` when the repo has no tasks.
 *
 * @since 1.0.0
 */
export class OrcaRepoSeen extends Schema.Class<OrcaRepoSeen>("OrcaRepoSeen")({
  repo: Schema.String,
  registered: Schema.Boolean,
  taskCount: Schema.Int,
  runningCount: Schema.Int,
  lastActivityAt: Schema.NullOr(Schema.DateTimeUtc),
  lastIssueIdentifier: Schema.NullOr(Schema.String),
}) {}
